import { Component, OnInit, ElementRef, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { SharedServiceService } from '../../../../services/shared-service.service';
import { debounceTime, filter, combineLatest, firstValueFrom } from 'rxjs';
import { AllServicesService } from '../../../../../services/all-services.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { LoggerService } from '../../../../services/logger.service';
import { NotificationFacadeService } from '../../../../services/notification-facade.service';
import { mapUnknownError } from '../../../../services/app-error-catalog';

interface TableRow {
  id: number;
  component: string;
  treatRate: string;
  treatRateRaw?: number | null;
  unit: string;
  family: string;
  wtInFO: number;
  wtInDI: number;
  blendMetal: string;
  typical: number;
  isManuallyAdded?: boolean; // Indicates whether the row was added manually
  [key: string]: string | number | boolean | null | undefined; // Add an index signature to allow dynamic access
}

@Component({
  selector: 'app-bc-fob-packages',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ScrollingModule, MatAutocompleteModule, MatInputModule],
  templateUrl: './bc-fob-packages.component.html',
  styleUrls: ['./bc-fob-packages.component.scss']
})

/**
 * Editable DI package grid.
 *
 * Data model uses package-first and blend-second indexing:
 * tableData[packageIndex][blendIndex] => rows[]
 *
 * The component keeps UI controls, backend recalculation calls, and SharedService
 * totals synchronized after every dosage/dilution/additive change.
 */
export class BcFobPackagesComponent implements OnInit {
  @Output() tableDataChange: EventEmitter<TableRow[][]> = new EventEmitter<TableRow[][]>();
  selectedBlend: number = 0;
  selectedPackage: number = 0;
  tableHeaders: string[] = ['', 'Component', 'Treat rate in FO', 'Unit', 'wt% in FO', 'wt% in DI', 'Blend Metal', 'Typical'];
  // Initialize with the correct structure: [package0[blend0[], blend1[]], package1[blend0[], blend1[]]]
  tableData: any[][] = [
    [[], []], // Package 0: [Blend 0, Blend 1]
    [[], []]  // Package 1: [Blend 0, Blend 1]
  ];
  packageIndex: number = 0;
  retrievedData: any[] = [];
  headerData: any[][] = [[], []];

  // Dynamic DI in FO [wt%] values for each package/blend combination
  // Structure: [package0[blend0, blend1], package1[blend0, blend1]]
  diInFOValues: number[][] = [[0, 0], [0, 0]];

  // Dynamic Dil. Oil in DI [wt%] values per package/blend
  // Structure: [package0[blend0, blend1], package1[blend0, blend1]]
  dilPerValues: number[][] = [[0, 0], [0, 0]];

  // Dropdown options for DI additives
  availableAdditives: string[] = [];

  componentControls: FormControl[][][] = [[[], []], [[], []]];
  filteredAdditives: string[][][][] = [[[], []], [[], []]];

  // Getter to obtain the current DI in FO [wt%] value for the selected package/blend
  get currentDiInFO(): number {
    return this.diInFOValues[this.selectedPackage]?.[this.selectedBlend] || 0;
  }

  // Getter to calculate Dil. Oil in FO [wt%] = DI in FO * (dil_per / 100)
  get currentDilOilInFO(): string {
    const diInFO = this.currentDiInFO;
    const dilPer = this.currentDilPer;
    return (diInFO * (dilPer / 100)).toFixed(2);
  }

  // Getter to obtain the current Dil. Oil in DI [wt%] value for the selected package/blend
  get currentDilPer(): number {
    return this.dilPerValues[this.selectedPackage]?.[this.selectedBlend] || 0;
  }

  constructor(private elementRef: ElementRef, private sharedService: SharedServiceService,
    private allService: AllServicesService,
    private logger: LoggerService,
    private notificationFacade: NotificationFacadeService
  ) {
  }

  /**
   * Shared operational-error path for user-triggered package actions.
   * Internal state inconsistencies do not call this helper because they are kept as
   * logger-only diagnostics to avoid unnecessary snackbar noise.
   */
  private notifyOperationError(error: unknown, source: string, context?: Record<string, unknown>): void {
    const appError = mapUnknownError(error, source);
    this.logger.error(appError, context);
    this.notificationFacade.showError(appError);
  }

  /**
   * Logger-only diagnostic path for local state mismatches that users cannot act on.
   */
  private logInternalWarning(message: string, context?: Record<string, unknown>): void {
    this.logger.warn(message, context);
  }

  private parseTreatRateRaw(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatTreatRateToFourDecimals(value: unknown): { valueStr: string; valueNum: number } {
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(parsed)) {
      return { valueStr: '0.0000', valueNum: 0 };
    }
    const truncated = Math.trunc(parsed * 10000) / 10000;
    return { valueStr: truncated.toFixed(4), valueNum: truncated };
  }

  private ensureComponentControlArrays(packageIndex: number, blendIndex: number): void {
    if (!this.componentControls[packageIndex]) {
      this.componentControls[packageIndex] = [[], []];
    }
    if (!this.filteredAdditives[packageIndex]) {
      this.filteredAdditives[packageIndex] = [[], []];
    }
    if (!this.componentControls[packageIndex][blendIndex]) {
      this.componentControls[packageIndex][blendIndex] = [];
    }
    if (!this.filteredAdditives[packageIndex][blendIndex]) {
      this.filteredAdditives[packageIndex][blendIndex] = [];
    }
  }

  private filterAdditives(value: string): string[] {
    const filterValue = (value || '').toLowerCase();
    return this.availableAdditives.filter(additive =>
      additive.toLowerCase().includes(filterValue)
    );
  }

  private syncComponentControls(packageIndex: number, blendIndex: number): void {
    this.ensureComponentControlArrays(packageIndex, blendIndex);
    const rows = this.tableData?.[packageIndex]?.[blendIndex] || [];
    const controls = this.componentControls[packageIndex][blendIndex];
    const filtered = this.filteredAdditives[packageIndex][blendIndex];

    rows.forEach((row: any, index: number) => {
      if (!controls[index]) {
        controls[index] = new FormControl(row?.component || '');
      } else if (row?.component && controls[index].value !== row.component) {
        controls[index].setValue(row.component, { emitEvent: false });
      }
      const currentValue = String(controls[index].value || '');
      filtered[index] = this.filterAdditives(currentValue);
    });

    controls.length = rows.length;
    filtered.length = rows.length;
  }

  private refreshAllFilteredAdditives(): void {
    for (let pkg = 0; pkg < this.componentControls.length; pkg++) {
      for (let blend = 0; blend < (this.componentControls[pkg]?.length || 0); blend++) {
        const controls = this.componentControls[pkg][blend] || [];
        const filtered = this.filteredAdditives[pkg][blend] || [];
        controls.forEach((control, index) => {
          const value = String(control?.value || '');
          filtered[index] = this.filterAdditives(value);
        });
      }
    }
  }

  getComponentControl(rowIndex: number): FormControl {
    const packageIndex = this.selectedPackage;
    const blendIndex = this.selectedBlend;
    this.ensureComponentControlArrays(packageIndex, blendIndex);

    const controls = this.componentControls[packageIndex][blendIndex];
    if (!controls[rowIndex]) {
      const currentValue = this.currentTableData?.[rowIndex]?.component || '';
      controls[rowIndex] = new FormControl(currentValue);
    }

    return controls[rowIndex];
  }

  getFilteredAdditives(rowIndex: number): string[] {
    const packageIndex = this.selectedPackage;
    const blendIndex = this.selectedBlend;
    this.ensureComponentControlArrays(packageIndex, blendIndex);

    const filtered = this.filteredAdditives[packageIndex][blendIndex];
    if (!filtered[rowIndex]) {
      const controlValue = String(this.getComponentControl(rowIndex).value || '');
      filtered[rowIndex] = this.filterAdditives(controlValue);
    }

    return filtered[rowIndex];
  }

  onComponentInput(rowIndex: number, value: string): void {
    const packageIndex = this.selectedPackage;
    const blendIndex = this.selectedBlend;
    this.ensureComponentControlArrays(packageIndex, blendIndex);
    this.filteredAdditives[packageIndex][blendIndex][rowIndex] = this.filterAdditives(value);
  }

  onComponentInputBlur(rowIndex: number): void {
    const previousComponent = String(this.currentTableData?.[rowIndex]?.component || '');
    const control = this.getComponentControl(rowIndex);
    const rawValue = String(control.value || '').trim();
    if (!rawValue) {
      control.setValue('', { emitEvent: false });
      return;
    }

    const match = this.availableAdditives.find(additive =>
      additive.toLowerCase() === rawValue.toLowerCase()
    );

    if (match) {
      control.setValue(match, { emitEvent: false });
      this.onAdditiveChange(rowIndex, match);
    } else {
      control.setValue(previousComponent, { emitEvent: false });
    }
  }

  onAdditiveSelected(event: MatAutocompleteSelectedEvent, rowIndex: number): void {
    const selectedAdditive = event.option.value as string;
    const control = this.getComponentControl(rowIndex);
    control.setValue(selectedAdditive, { emitEvent: false });
    this.onAdditiveChange(rowIndex, selectedAdditive);
  }

  // Getter to safely obtain current table data
  get currentTableData(): TableRow[] {
    if (!this.tableData[this.selectedPackage]) {
      return [];
    }
    if (!this.tableData[this.selectedPackage][this.selectedBlend]) {
      return [];
    }
    return this.tableData[this.selectedPackage][this.selectedBlend];
  }

  ngOnInit() {
    // Load dropdown options on initialization
    this.loadDropdownOptions();

    // Hydrate table from SharedService when a favorite is imported
    this.sharedService.ImpFavoriteFlag$
      .pipe(filter(flag => flag === true))
      .subscribe(() => this.hydrateFromFavoriteImport());

    this.sharedService.retrievedPackageBlendUpdate$
      .pipe(
        filter((update): update is { packageIndex: number; blendIndex: number; rows: any[]; requestedTreat?: number } =>
          !!update &&
          typeof update.packageIndex === 'number' &&
          typeof update.blendIndex === 'number' &&
          Array.isArray(update.rows)
        )
      )
      .subscribe((update) => {
        const { packageIndex, blendIndex, rows, requestedTreat } = update;

        if (!this.tableData[packageIndex]) {
          this.tableData[packageIndex] = [[], []];
        }

        const mappedRows = this.mapBackendRowsToTableRows(rows);
        this.tableData[packageIndex][blendIndex] = mappedRows;
        this.syncSharedRows(packageIndex, blendIndex, mappedRows);
        this.syncComponentControls(packageIndex, blendIndex);

        const dilVal = Number(this.headerData?.[packageIndex]?.[2]) || 0;
        const sumWtFo = mappedRows.reduce((sum, row) => sum + (Number(row.wtInFO) || 0), 0);
        const treatWt = (dilVal > 0 && dilVal < 100)
          ? sumWtFo + (dilVal / (100 - dilVal)) * sumWtFo
          : sumWtFo;
        // Use the user-requested treat verbatim when provided; recomputing from the
        // individually-rounded components drifts by +/-0.01 against what was typed.
        const treatWtRounded = (typeof requestedTreat === 'number' && !isNaN(requestedTreat))
          ? parseFloat(requestedTreat.toFixed(2))
          : parseFloat(treatWt.toFixed(2));
        this.diInFOValues[packageIndex][blendIndex] = treatWtRounded;
        this.syncSharedTotals(packageIndex, blendIndex, treatWtRounded, dilVal);

        if (this.selectedPackage === packageIndex && this.selectedBlend === blendIndex) {
          this.emitUpdatedPackageData(requestedTreat);
        } else {
          this.emitTableData();
        }
      });

    combineLatest([
      this.sharedService.retrievedPackageData$,
      this.sharedService.retrievedPackageIndex$
    ]).pipe(
      debounceTime(100), // Wait 100ms to avoid multiple updates
      filter(([data, index]) => Array.isArray(data) && typeof index === 'number')
    ).subscribe(async ([data, index]) => {
      this.packageIndex = index;

      // Ignore default retrievedPackageData while a favorite import is hydrating the table
      if (this.sharedService.getIsHydratingFavoritePackages()) {
        return;
      }

      // FIRST: Clear ALL packages that are null in the full array
      data.forEach((pkg, pkgIndex) => {
        if (!pkg || pkg === null) {
          this.headerData[pkgIndex] = [];
          this.tableData[pkgIndex] = [[], []];
          this.diInFOValues[pkgIndex] = [0, 0];
          this.dilPerValues[pkgIndex] = [0, 0];
        }
      });

      // SECOND: If the CURRENT package is null, just emit and exit
      if (!data[index] || data[index] === null) {
        this.emitTableData();
        return;
      }

      // THIRD: If the current package has data, process it normally
      this.headerData[index] = data[index];
      const auxArrayTableRow = await this.handlePackageDataChange(data);

      // Clone each object to separate blends
      const blend0Data = auxArrayTableRow.map(row => ({ ...row }));
      const blend1Data = auxArrayTableRow.map(row => ({ ...row }));
      this.tableData[index] = [blend0Data, blend1Data];
      this.syncComponentControls(index, 0);
      this.syncComponentControls(index, 1);

      // Seed both blend row observables so an un-edited blend is not saved empty.
      // Blend-specific edits later overwrite these via setRetrievedPackageBlendUpdate.
      this.syncSharedRows(index, 0, blend0Data);
      this.syncSharedRows(index, 1, blend1Data);

      // Initialize diInFOValues with the backend value (headerData[packageIndex][1])
      // This value is shown as "DI in FO [wt%]" and used to calculate "Dil. Oil in FO [wt%]"
      const initialDiInFO = Number(this.headerData[index]?.[1]) || 0;
      this.diInFOValues[index][0] = initialDiInFO; // Blend 0
      this.diInFOValues[index][1] = initialDiInFO; // Blend 1
      const initialDilVal = Number(this.headerData[index]?.[2]) || 0;
      this.dilPerValues[index][0] = initialDilVal;
      this.dilPerValues[index][1] = initialDilVal;

      this.syncSharedTotals(index, 0, initialDiInFO, this.dilPerValues[index][0]);
      this.syncSharedTotals(index, 1, initialDiInFO, this.dilPerValues[index][1]);

      // Emit data to the parent for Execute Prediction button validation
      this.emitTableData();
    });

    // Subscription for treatRate-specific changes
    this.sharedService.treatRateChange$.subscribe(change => {
      if (change && change.packageIndex !== undefined) {
        // Update the table with the new value
        this.updateTreatRateInTable(
          change.packageIndex,
          change.blendIndex,
          change.componentIndex,
          change.newValue
        );
      }
    });
    setTimeout(() => this.emitTableData(), 0);
  }

  // #region Row editing and backend synchronization

  private async handlePackageDataChange(data: any[]): Promise<TableRow[]> {
    try {
      const packageData = this.mapBackendRowsToTableRows(data[this.packageIndex][3]);
      return packageData;
    } catch (error) {
      const appError = mapUnknownError(error, 'handlePackageDataChange');
      this.logger.error(appError, {
        packageIndex: this.packageIndex,
        operation: 'handlePackageDataChange'
      });
      return [];
    }
  }

  private mapBackendRowsToTableRows(rows: any[]): TableRow[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item: any, index: number) => ({
      id: index + 1,
      component: item.Additive || item.component || '',
      treatRateRaw: this.parseTreatRateRaw(item.Dosage ?? item.treatRate),
      treatRate: (() => {
        const raw = this.parseTreatRateRaw(item.Dosage ?? item.treatRate);
        return raw !== null ? this.formatTreatRateToFourDecimals(raw).valueStr : '';
      })(),
      unit: item.Unit || item.unit || '',
      family: item.Family || item.family || '',
      wtInFO: Number(item['Wt% in Finished Oil'] ?? item.wtInFO) || 0.0000,
      wtInDI: Number(item['Wt% in DI Package'] ?? item.wtInDI) || 0.0000,
      blendMetal: item.Metal || item.blendMetal || 'text',
      typical: Number(item['Typ. Metal %'] ?? item.Typ_Metal ?? item.typical) || 0.0000,
      isManuallyAdded: item.isManuallyAdded || false
    }));
  }

  private syncSharedRows(packageIndex: number, blendIndex: number, rows: TableRow[]): void {
    if (packageIndex === 0) {
      if (blendIndex === 0) {
        this.sharedService.setPackage1Pred1(rows);
      } else {
        this.sharedService.setPackage1Pred2(rows);
      }
    } else {
      if (blendIndex === 0) {
        this.sharedService.setPackage2Pred1(rows);
      } else {
        this.sharedService.setPackage2Pred2(rows);
      }
    }
  }

  private syncSharedTotals(packageIndex: number, blendIndex: number, treatWt: number, dilVal: number): void {
    if (packageIndex === 0) {
      if (blendIndex === 0) {
        this.sharedService.settotal_package_1_pred_1(treatWt);
        this.sharedService.settotal_dil_per_package_1_pred_1(dilVal);
      } else {
        this.sharedService.settotal_package_1_pred_2(treatWt);
        this.sharedService.settotal_dil_per_package_1_pred_2(dilVal);
      }
    } else {
      if (blendIndex === 0) {
        this.sharedService.settotal_package_2_pred_1(treatWt);
        this.sharedService.settotal_dil_per_package_2_pred_1(dilVal);
      } else {
        this.sharedService.settotal_package_2_pred_2(treatWt);
        this.sharedService.settotal_dil_per_package_2_pred_2(dilVal);
      }
    }
  }

  /**
   * Populate dropdown options from API
    *
    * This is a component-load failure path, so it now uses the same user-facing
    * snackbar strategy as other operational errors in the error-handling branch.
   */
  private loadDropdownOptions(): void {
    this.allService.feature_importance_data().subscribe({
      next: (data) => {
      }
    });
    this.allService.getdropdown().subscribe({
      next: (data) => {
        // Extract DI additive names from the dropdown
        if (data && data.DI && Array.isArray(data.DI)) {
          this.availableAdditives = data.DI.map((item: any) => item.Additive).filter(Boolean);
          this.refreshAllFilteredAdditives();
        }
      },
      error: (error) => {
        this.notifyOperationError(error, 'loadDropdownOptions', {
          operation: 'loadDropdownOptions'
        });
      }
    });
  }

  // Add this method
  updateTreatRateInTable(packageIndex: number, blendIndex: number, componentIndex: number, newValue: number): void {
    // If we are currently displaying data from the affected package and blend
    if (this.selectedPackage === packageIndex && this.selectedBlend === blendIndex) {
      if (this.tableData[packageIndex] &&
        this.tableData[packageIndex][blendIndex] &&
        this.tableData[packageIndex][blendIndex][componentIndex]) {

        // Create a copy of the object to preserve immutability
        const updatedRow = { ...this.tableData[packageIndex][blendIndex][componentIndex] };
        updatedRow.treatRateRaw = Number.isFinite(newValue) ? newValue : null;
        updatedRow.treatRate = Number.isFinite(newValue)
          ? this.formatTreatRateToFourDecimals(newValue).valueStr
          : '';

        // Update the row
        this.tableData[packageIndex][blendIndex][componentIndex] = updatedRow;

        // Emit updated data
        this.emitTableData();
      }
    }
  }

  addRow(): void {
    // Ensure structure before calculating id
    if (!this.tableData[this.selectedPackage]) {
      this.tableData[this.selectedPackage] = [[], []];
    }
    if (!this.tableData[this.selectedPackage][this.selectedBlend]) {
      this.tableData[this.selectedPackage][this.selectedBlend] = [];
    }
    // Calculate id based on existing rows so it stays consecutive
    const existingRows = this.tableData[this.selectedPackage][this.selectedBlend];
    const newId = existingRows.length + 1;
    // Create an empty row
    const newRow: TableRow = {
      id: newId,
      component: '', // Empty so the dropdown is shown
      treatRate: '',
      treatRateRaw: null,
      unit: '',
      family: '',
      wtInFO: 0,
      wtInDI: 0,
      blendMetal: '',
      typical: 0,
      isManuallyAdded: true // Flag as manually added
    };
    existingRows.push(newRow);
    this.syncComponentControls(this.selectedPackage, this.selectedBlend);
  }

  /**
  * Removes a row from the table
   */
  deleteRow(rowIndex: number): void {
    if (this.tableData[this.selectedPackage]?.[this.selectedBlend]) {
      this.tableData[this.selectedPackage][this.selectedBlend].splice(rowIndex, 1);
      const controls = this.componentControls?.[this.selectedPackage]?.[this.selectedBlend];
      const filtered = this.filteredAdditives?.[this.selectedPackage]?.[this.selectedBlend];
      if (controls) {
        controls.splice(rowIndex, 1);
      }
      if (filtered) {
        filtered.splice(rowIndex, 1);
      }
      this.emitUpdatedPackageData();
    }
  }

  /**
   * Handles the change in the component dropdown
  * Calls the API to fetch full row data
   */
  onAdditiveChange(rowIndex: number, selectedAdditive: string): void {
    // Update the component in the row
    if (this.tableData[this.selectedPackage]?.[this.selectedBlend]?.[rowIndex]) {
      this.tableData[this.selectedPackage][this.selectedBlend][rowIndex].component = selectedAdditive;
    }

    // Prepare the payload for the API
    const payload = {
      package: [this.selectedPackage + 1], // Backend uses 1-based indexes
      prediction: [this.selectedBlend + 1],
      Additive: [selectedAdditive]
    };

    // Call the API to fetch full row data
    this.allService.get_DI_update(payload).subscribe({
      next: (response) => {
        // The response is an array with updated data
        if (Array.isArray(response) && response.length > 0) {
          // Update the row with the received data
          const updatedData = response.find((item: any) => item.Additive === selectedAdditive);
          if (updatedData && this.tableData[this.selectedPackage]?.[this.selectedBlend]?.[rowIndex]) {
            const row = this.tableData[this.selectedPackage][this.selectedBlend][rowIndex];
            row.component = updatedData.Additive || selectedAdditive;
            const updatedTreatRate = updatedData['Treat in Finished Oil'] ?? updatedData.Dosage;
            const rawTreatRate = this.parseTreatRateRaw(updatedTreatRate);
            row.treatRateRaw = rawTreatRate;
            row.treatRate = rawTreatRate !== null
              ? this.formatTreatRateToFourDecimals(rawTreatRate).valueStr
              : '';
            row.unit = updatedData.Unit || '';
            row.family = updatedData.Family || '';
            row.wtInFO = updatedData['Wt% in Finished Oil'] || 0;
            row.wtInDI = updatedData['Wt% in DI Package'] || 0;
            row.blendMetal = updatedData.Metal || '';
            row.typical = updatedData['Typ. Metal %'] || 0;

            // Emit updated data
            this.emitUpdatedPackageData();
          }
        }
      },
      error: (error) => {
        this.notifyOperationError(error, 'onAdditiveChange', {
          operation: 'onAdditiveChange',
          selectedPackage: this.selectedPackage,
          selectedBlend: this.selectedBlend,
          rowIndex,
          selectedAdditive
        });
      }
    });
  }

  // Intermediate method to safely handle the input event
  handleInputChange(event: Event, rowIndex: number, property: string): void {
    const target = event.target as HTMLInputElement;
    if (target) {
      this.updateCellValue(rowIndex, property, target.value);
    }
    this.emitTableData();
  }

  // Method to update a specific cell value
  updateCellValue(rowIndex: number, property: string, value: string | number | null): void {
    // Validate that the full structure exists
    if (!this.tableData[this.selectedPackage] ||
      !this.tableData[this.selectedPackage][this.selectedBlend] ||
      !this.tableData[this.selectedPackage][this.selectedBlend][rowIndex]) {
      this.logInternalWarning('Cannot update package cell because the table structure is invalid', {
        selectedPackage: this.selectedPackage,
        selectedBlend: this.selectedBlend,
        rowIndex
      });
      return;
    }

    // Create a copy to avoid direct mutations
    const updatedRow = { ...this.tableData[this.selectedPackage][this.selectedBlend][rowIndex] };
    updatedRow[property] = value;
    this.tableData[this.selectedPackage][this.selectedBlend][rowIndex] = updatedRow;
  }

  emitTableData(): void {
    this.tableDataChange.emit(this.tableData);
  }

  // Also call it when the selected package changes
  onPackageChange(packageIndex: number): void {
    this.selectedPackage = packageIndex;
    this.syncComponentControls(this.selectedPackage, this.selectedBlend);
    this.emitTableData();
  }

  onBlendChange(blendIndex: number): void {
    this.selectedBlend = blendIndex;
    this.syncComponentControls(this.selectedPackage, this.selectedBlend);
    this.emitTableData();
  }

  scrollToPC() {
    const element = this.elementRef.nativeElement;
    element.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  // Improved trackBy function that uses a unique identifier for each row
  trackByRowId(index: number, item: TableRow): number {
    return item.id;
  }

  // Keep this function for compatibility if it is used elsewhere
  trackByIndex(index: number, item: any): number {
    return index;
  }
  // Handles changes in treatRate (Dosage)
  handleTreatRateChange(event: Event, rowIndex: number): void {
    const target = event.target as HTMLInputElement;
    if (!target) return;

    const rawTreatRate = this.parseTreatRateRaw(target.value);
    const displayValue = rawTreatRate !== null
      ? this.formatTreatRateToFourDecimals(rawTreatRate).valueStr
      : '';

    // Update the value in the table (display) and preserve the full raw value
    this.updateCellValue(rowIndex, 'treatRate', displayValue);
    this.updateCellValue(rowIndex, 'treatRateRaw', rawTreatRate);

    // Prepare data for the API call with the raw value
    this.updatePackageTableData(rowIndex, rawTreatRate ?? 0);
  }

  // Method to update the package table data
  updatePackageTableData(rowIndex: number, newTreatValue: number): void {
    // 1. Collect required data
    const currentPackage = this.selectedPackage;
    const currentBlend = this.selectedBlend;

    // Validate that the data structure exists
    if (!this.tableData[currentPackage] ||
      !this.tableData[currentPackage][currentBlend] ||
      !Array.isArray(this.tableData[currentPackage][currentBlend])) {
      this.logInternalWarning('Package table structure is invalid during treat rate update', {
        currentPackage,
        currentBlend,
        tableData: this.tableData
      });
      return;
    }

    // Arrays for the API call
    const additiveNames: string[] = [];
    const dosageValues: number[] = [];
    const predNumArray: number[] = [];
    const packageNumArray: number[] = [];

    // Fill arrays with current table data
    this.tableData[currentPackage][currentBlend].forEach((row: any, idx: number) => {
      if (row.component) {
        additiveNames.push(row.component);

        // If this is the row we are updating, use the new value
        if (idx === rowIndex) {
          dosageValues.push(newTreatValue);
        } else {
          const rawValue = this.parseTreatRateRaw(row.treatRateRaw);
          dosageValues.push(rawValue ?? (parseFloat(row.treatRate) || 0));
        }

        predNumArray.push(this.selectedBlend + 1); // 0->1, 1->2
        packageNumArray.push(this.selectedPackage + 1); // 0->1, 1->2
      }
    });

    // Get dilution oil percentage
    const dilPercentage = this.dilPerValues[currentPackage]?.[currentBlend] || 0;

    // 2. Create the object for the API call
    const packageTableData = {
      di_inp: {
        prediction: predNumArray,
        package: packageNumArray,
        additive: additiveNames,
        dosage: dosageValues
      },
      dil_oil: {
        pred_num: this.selectedBlend + 1,
        package: [this.selectedPackage + 1],
        dil_per: [dilPercentage]
      },
      pred_num: this.selectedBlend + 1,
      package: this.selectedPackage + 1
    };

    // 3. Call the service
    // Capture indices to avoid race conditions if the user changes blends before the response arrives
    this.allService.get_di_wt_update(packageTableData).subscribe({
      next: (data: any[]) => {
        // Use the captured indices
        this.updateTableWithApiResponse(data, currentPackage, currentBlend);
      },
      error: (err) => {
        this.notifyOperationError(err, 'updatePackageTableData', {
          operation: 'updatePackageTableData',
          currentPackage,
          currentBlend,
          rowIndex
        });
      }
    });
  }

  // Method to update the table with the API response
  updateTableWithApiResponse(data: any[], packageIndex: number, blendIndex: number): void {
    if (!data || data.length === 0) return;

    // Map response data to the table format
    // Preserve isManuallyAdded and family from previous rows
    const prevRows: any[] = this.tableData?.[packageIndex]?.[blendIndex] || [];
    const updatedRows = data.map((item: any, index: number) => {
      const rawTreatRate = this.parseTreatRateRaw(
        item.Dosage ?? prevRows[index]?.treatRateRaw
      );
      const treatRateValue = rawTreatRate !== null
        ? this.formatTreatRateToFourDecimals(rawTreatRate).valueStr
        : (prevRows[index]?.treatRate ?? '');
      return {
        id: index + 1,
        component: item.Additive || '',
        // If the response does not include Dosage, preserve the previous treatRate value
        treatRate: treatRateValue,
        treatRateRaw: rawTreatRate,
        unit: item.Unit || '',
        family: item.Family || prevRows[index]?.family || '',
        wtInFO: typeof item['Wt% in Finished Oil'] === 'number' ?
          item['Wt% in Finished Oil'] : 0,
        wtInDI: typeof item['Wt% in DI Package'] === 'number' ?
          item['Wt% in DI Package'] : 0,
        blendMetal: item.Metal || '',
        typical: typeof item['Typ. Metal %'] === 'number' ?
          item['Typ. Metal %'] : 0,
        isManuallyAdded: prevRows[index]?.isManuallyAdded || false
      };
    });

    // Update the table using the captured indices
    this.tableData[packageIndex][blendIndex] = updatedRows;
    this.syncComponentControls(packageIndex, blendIndex);

    // Emit updated data and calculate totals ONLY if we are still on the same package/blend
    if (this.selectedPackage === packageIndex && this.selectedBlend === blendIndex) {
      this.emitUpdatedPackageData();
    }
  }

  // Handles changes in dilution percentage
  handleDilutionChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target) return;

    const newValue = parseFloat(target.value);
    if (isNaN(newValue)) return;

    // Get the previous value
    const oldValue = this.dilPerValues[this.selectedPackage]?.[this.selectedBlend];

    // It is updated only if the value actually changed
    if (oldValue === newValue) {
      return;
    }

    // Update the value in the array by blend
    if (!this.dilPerValues[this.selectedPackage]) {
      this.dilPerValues[this.selectedPackage] = [0, 0];
    }
    this.dilPerValues[this.selectedPackage][this.selectedBlend] = newValue;

    // Prepare the data to update the table
    this.updateDilutionData(newValue);
  }

  // Method to update data based on a dilution change
  updateDilutionData(dilPercentage: number): void {
    // Capture indices to avoid race conditions
    const currentPackage = this.selectedPackage;
    const currentBlend = this.selectedBlend;

    // Build the same arrays as in updatePackageTableData
    const additiveNames: string[] = [];
    const dosageValues: number[] = [];
    const predNumArray: number[] = [];
    const packageNumArray: number[] = [];

    // Iterate over current rows of the selected blend
    const rows = this.currentTableData;
    rows.forEach((row) => {
      if (row && (row as any).component) {
        additiveNames.push((row as any).component as string);
        const rawValue = this.parseTreatRateRaw((row as any).treatRateRaw);
        dosageValues.push(rawValue ?? (parseFloat((row as any).treatRate as string) || 0));
        predNumArray.push(this.selectedBlend + 1);
        packageNumArray.push(this.selectedPackage + 1);
      }
    });

    // Create the object for the API call
    const packageTableData = {
      di_inp: {
        prediction: predNumArray,
        package: packageNumArray,
        additive: additiveNames,
        dosage: dosageValues
      },
      dil_oil: {
        pred_num: this.selectedBlend + 1,
        package: [this.selectedPackage + 1],
        dil_per: [dilPercentage]
      },
      pred_num: this.selectedBlend + 1,
      package: this.selectedPackage + 1
    };

    // Call the service
    // IMPORTANT: Same here, use captured indices to avoid race conditions
    this.allService.get_di_wt_update(packageTableData).subscribe({
      next: (data: any[]) => {
        this.updateTableWithApiResponse(data, currentPackage, currentBlend);
      },
      error: (err) => {
        this.notifyOperationError(err, 'updateDilutionData', {
          operation: 'updateDilutionData',
          currentPackage,
          currentBlend,
          dilPercentage
        });
      }
    });
  }

  calculateTotals(): { wtInFOTotal: number, wtInDITotal: number } {
    let wtInFOTotal = 0;
    let wtInDITotal = 0;

    // Verify that data exists to avoid errors
    const rows = this.currentTableData;
    if (rows && rows.length > 0) {
      rows.forEach((row: any) => {
        wtInFOTotal += Number(row?.wtInFO) || 0;
        wtInDITotal += Number(row?.wtInDI) || 0;
      });
    }

    // Calculate the dilution oil contribution in FO using only rows + dil%:
    // If sum(wtInFO) represents the undiluted part (100 - dil%) of the package in FO,
    // then the dilution contribution in FO = sumFO * dil% / (100 - dil%).
    const dilPercent = this.currentDilPer;
    let dilContributionFO = 0;
    if (dilPercent > 0 && dilPercent < 100) {
      dilContributionFO = wtInFOTotal * (dilPercent / (100 - dilPercent));
    }

    // Totales
    let totalinfo = wtInFOTotal;
    let totalindi = wtInDITotal + dilPercent;

    return {
      wtInFOTotal: parseFloat(totalinfo.toFixed(2)),
      wtInDITotal: parseFloat(totalindi.toFixed(2))
    };
  }

  // Emit data when dosage or dilution changes
  emitUpdatedPackageData(requestedTreat?: number): void {
    // Notify the shared service about the changes
    const rows = this.currentTableData;
    if (this.selectedPackage === 0) {
      if (this.selectedBlend === 0) {
        this.sharedService.setPackage1Pred1(rows);
      } else {
        this.sharedService.setPackage1Pred2(rows);
      }
    } else {
      if (this.selectedBlend === 0) {
        this.sharedService.setPackage2Pred1(rows);
      } else {
        this.sharedService.setPackage2Pred2(rows);
      }
    }

    // Emit to the parent component
    this.tableDataChange.emit(this.tableData);

    // Calculate the sum of component "Wt% in Finished Oil"
    const totals = this.calculateTotals();
    const sumaComponentes = totals.wtInFOTotal;

    // Get dil_per from the selected package
    const dilVal = this.currentDilPer;

    // This calculates "Treat [wt%]" (DI in FO) including the Dilution Oil contribution
    let treatWt = sumaComponentes;
    if (dilVal > 0 && dilVal < 100) {
      treatWt = sumaComponentes + (dilVal / (100 - dilVal)) * sumaComponentes;
    }
    treatWt = parseFloat(treatWt.toFixed(2));

    // Use the user-requested treat verbatim when provided; recomputing from the
    // individually-rounded components drifts by +/-0.01 against what was typed.
    if (typeof requestedTreat === 'number' && !isNaN(requestedTreat)) {
      treatWt = parseFloat(requestedTreat.toFixed(2));
    }

    // Update the local DI in FO value for the current package/blend
    this.diInFOValues[this.selectedPackage][this.selectedBlend] = treatWt;

    // Emit the calculated value to the selected package/blend
    this.syncSharedTotals(this.selectedPackage, this.selectedBlend, treatWt, dilVal);
  }

  // #endregion

  /**
   * Hydrate the packages table from SharedService observables after a favorite import.
   * Reads package_*_pred_*$ and Dil_wt_percent_* to rebuild tableData and totals.
   */
  private async hydrateFromFavoriteImport(): Promise<void> {
    // Clear existing table state before importing
    this.tableData = [[[], []], [[], []]];
    this.diInFOValues = [[0, 0], [0, 0]];
    this.dilPerValues = [[0, 0], [0, 0]];

    const sources: Array<{ pkg: number; blend: number; data$: any; dil$: any }> = [
      { pkg: 0, blend: 0, data$: this.sharedService.package_1_pred_1$, dil$: this.sharedService.Dil_wt_percent_Pack1_pred1$ },
      { pkg: 0, blend: 1, data$: this.sharedService.package_1_pred_2$, dil$: this.sharedService.Dil_wt_percent_Pack1_pred2$ },
      { pkg: 1, blend: 0, data$: this.sharedService.package_2_pred_1$, dil$: this.sharedService.Dil_wt_percent_Pack2_pred1$ },
      { pkg: 1, blend: 1, data$: this.sharedService.package_2_pred_2$, dil$: this.sharedService.Dil_wt_percent_Pack2_pred2$ },
    ];

    for (const src of sources) {
      const rows = await firstValueFrom(src.data$);
      const dilRaw = await firstValueFrom(src.dil$);
      const dilVal = Number(dilRaw) || 0;

      if (!rows || !Array.isArray(rows) || rows.length === 0) {
        this.tableData[src.pkg][src.blend] = [];
        this.diInFOValues[src.pkg][src.blend] = 0;
        this.dilPerValues[src.pkg][src.blend] = 0;
        this.syncSharedTotals(src.pkg, src.blend, 0, 0);
        continue;
      }

      const mappedRows = this.mapBackendRowsToTableRows(rows);
      this.tableData[src.pkg][src.blend] = mappedRows;
      this.syncComponentControls(src.pkg, src.blend);
      this.dilPerValues[src.pkg][src.blend] = dilVal;

      const sumWtFo = mappedRows.reduce((sum, row) => sum + (Number(row.wtInFO) || 0), 0);
      const treatWt = (dilVal > 0 && dilVal < 100)
        ? sumWtFo + (dilVal / (100 - dilVal)) * sumWtFo
        : sumWtFo;
      const treatWtRounded = parseFloat(treatWt.toFixed(2));
      this.diInFOValues[src.pkg][src.blend] = treatWtRounded;
      this.syncSharedTotals(src.pkg, src.blend, treatWtRounded, dilVal);
    }

    this.emitTableData();
    // Keep ImpFavoriteFlag set: the model-input panels (base oil, package, VM, PPD) mount only
    // after their dropdowns load, which can happen after this hydration (e.g. shared-link tab).
    // As a BehaviorSubject, the flag replays to those late subscribers so they hydrate too.
    // A subsequent import re-emits true, so re-triggering still works.
  }
}